import { ComponentProps, ReactNode, useEffect } from 'react';
import Drawer, { drawerClasses } from '@mui/material/Drawer';
import { styled } from '@mui/material';
import Stack from '@mui/material/Stack';
import List from '@mui/material/List';
import { drawerWidth } from '../../../theme';
import { useNavigate } from 'react-router-dom';
import { SidebarMenuButton, SidebarSubMenuItem } from '@molecules/navigation';
import { AppLogo, AppLogoText, AppVersionLabel } from '@atoms/sidebar';
import logoImage from '@assets/logo_ax.png';
import logoText from '@assets/logoText_ax.svg';
import { BUILD_INFO } from '@libs/config';
import { useRefreshSyncStore } from '@libs/store/refreshSync';
import { assignRequestsConflictCount } from '@libs/api/shifts';

type SidebarProps = {
  topItems?: MenuItem[];
  bottomItems?: MenuItem[];
};
export const Sidebar = ({ topItems = [], bottomItems = [] }: SidebarProps) => {
  const { refreshTrigger } = useRefreshSyncStore();

  useEffect(() => {
    const fetcher = async () => {
      const count = await assignRequestsConflictCount();
      topItems.forEach((item, idx) => {
        if (item.id === 'shifts') {
          topItems[idx].badgeCount = count || 0;
          topItems[idx].badgeTooltip = 'There are ' + count + ' shifts with assignment time conflicts.';
        }
      });
    };
    fetcher();
  }, [topItems, refreshTrigger]);

  return (
    <SidebarDrawer aria-label="SidebarDrawer" variant="permanent" anchor="left">
      <Stack direction="row" justifyContent="center">
        <AppLogo logoImg={logoImage} />
      </Stack>

      <ListBoxContainer mt={2} direction="column" aria-label="SidebarBaseLogoContainer" justifyContent="space-between">
        <List aria-label="SidebarTopListContainer">
          <Stack gap={1}>
            {topItems.map((props, idx) => (
              <SidebarItem {...props} key={idx} />
            ))}
          </Stack>
        </List>

        <Stack direction="column" alignItems="center">
          <List aria-label="SidebarBottomListContainer">
            <Stack gap={1}>
              {bottomItems.map((props, idx) => (
                <SidebarItem {...props} key={idx} />
              ))}
            </Stack>
          </List>
        </Stack>
      </ListBoxContainer>
      <AppLogoText logoImg={logoText} />
      <AppVersionLabel version={BUILD_INFO.APP_VERSION} />
    </SidebarDrawer>
  );
};

export const SidebarItem = ({
  id,
  path,
  icon,
  label,
  children,
  anchorPosition,
  badgeCount,
  badgeTooltip,
  onClick,
}: MenuItem) => {
  const navigate = useNavigate();

  return (
    <SidebarMenuButton
      active={path ? location.pathname.includes(`/${path}`) : location.pathname === '/'}
      icon={icon}
      label={label}
      badgeCount={badgeCount}
      badgeTooltip={badgeTooltip}
      children={children || []}
      anchorPosition={anchorPosition}
      onClick={() => {
        if (onClick) {
          onClick();
          return; // break execution in case if onClick defined
        }
        if (!path) {
          navigate(`/`, { replace: true });
        }
        if (!location.pathname.includes(`/${path}`)) {
          navigate(`/${path}`);
        }
      }}
    />
  );
};

export type MenuItem = {
  id: string;
  label: ReactNode;
  badgeCount?: number;
  badgeTooltip?: string;
  icon: ComponentProps<typeof SidebarMenuButton>['icon'];
  path?: string;
  children?: SidebarSubMenuItem[];
  onClick?: () => void;
  // anchor position for the context submenu
  anchorPosition?: {
    vertical: 'top' | 'center' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
};

const ListBoxContainer = styled(Stack)(({ theme }) => ({
  height: '100%',
  width: '100%',
  overflow: 'auto',
  scrollbarGutter: '7px',
  [`::-webkit-scrollbar`]: {
    width: '3px',
    height: '3px',
    borderRadius: '3px',
  },
}));

const SidebarDrawer = styled(Drawer)`
  width: ${drawerWidth}px;
  max-height: '100%',
  padding-left: 0,
  padding-right: 0,
  flex-shrink: 0;
  & .${drawerClasses.paper} {
    padding-top: 12px;
    padding-bottom: 12px;
    scrollbar-gutter: 7px;
    padding-left: 0;
    padding-right: 0;
    width: ${drawerWidth}px;
    box-sizing: border-box;
    border-radius: 0 16px 16px 0;
    border: none;
  }
`;
